import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayProgram, SprayProduct, SprayRound, RoundProductOverride } from '../../services/sprayService';
import { listGlobalCropTypes, CropTypeNode } from '../../services/cropTypeService';
import { listPests, PestRef } from '../../services/pestService';
import { lineageOf } from '../../utils/cropLineage';
import { resolveSprayApplication } from '../../utils/sprayApplicationResolver';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import PageHeader from '../shell/PageHeader';
import {
  Field,
  FormSection,
  FormShell,
  FormShellSection,
  SaveBar,
  SaveBarAction,
  SaveBarStatus,
} from '../patterns';
import { RoundProductOverrideEditor } from './RoundProductOverrideEditor';

interface Props {
  program?: SprayProgram;
  onSave: (program: SprayProgram) => void;
  onCancel: () => void;
}

/**
 * Form-internal round shape. The on-disk model has both a deprecated
 * `productId` and a new `products` array; this form stores everything as
 * `products` (full RoundProductOverride subdocs) so multi-select interactions
 * and per-product overrides stay simple. Legacy programs (single `productId`)
 * are normalised on hydration so the user sees one selected product.
 * Submissions always write the `products` array as subdoc objects.
 */
interface RoundFormState {
  _id?: string;
  products: RoundProductOverride[];
  waterPerHectare?: number;
  plannedDate: string;
  name?: string;
  seq?: number;
}

const emptyRound = (defaultDate: string): RoundFormState => ({
  products: [],
  plannedDate: defaultDate || new Date().toISOString().substring(0, 10),
  waterPerHectare: undefined,
  name: '',
});

/**
 * Normalise a raw product entry from the server into a RoundProductOverride
 * subdoc. Handles three incoming shapes:
 *   1. Plain string ObjectId (legacy rounds or pre-subdoc documents) → wrap.
 *   2. Already a subdoc object with productId → pass through.
 *   3. Anything else → null (filtered out below).
 */
function normaliseProductEntry(entry: string | RoundProductOverride): RoundProductOverride | null {
  if (!entry) return null;
  if (typeof entry === 'string') return { productId: entry, targetPests: [] };
  if (typeof entry === 'object' && entry.productId) return entry as RoundProductOverride;
  return null;
}

const hydrateRound = (r: SprayRound): RoundFormState => {
  // Prefer products[]; fall back to [productId] for legacy single-product
  // rounds; final fallback to productList (server-injected on lean reads)
  // when neither shape is present locally.
  const rawProducts = (r.products && r.products.length > 0)
    ? r.products
    : (r.productId ? [r.productId] : (r.productList || []));
  const products = (rawProducts as Array<string | RoundProductOverride>)
    .map(normaliseProductEntry)
    .filter((e): e is RoundProductOverride => e !== null);
  return {
    _id: r._id,
    products,
    plannedDate: r.plannedDate?.substring(0, 10) ?? '',
    waterPerHectare: r.waterPerHectare,
    name: r.name,
    seq: r.seq,
  };
};

const SprayProgramForm: React.FC<Props> = ({ program, onSave, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = !!program;

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.programs.title'), to: '/protection/programs' },
    { label: isEdit
        ? t('spray.programs.editTitle', { name: program?.name ?? '' })
        : t('spray.pageHeader.actions.newProgram') },
  ]);

  /**
   * Legacy detection: a program is legacy if any of its rounds lacks a
   * `products` array (or has an empty one). Mirrors the backend predicate
   * in PUT /spray/programs/:id which returns 403 for the same condition.
   * We surface this client-side so the user gets immediate feedback rather
   * than a save-time error.
   */
  const isLegacyProgram = !!program && Array.isArray(program.rounds)
    && program.rounds.some(r => !Array.isArray(r.products) || r.products.length === 0);

  const [products, setProducts] = useState<SprayProduct[]>([]);
  const [cropTypes, setCropTypes] = useState<CropTypeNode[]>([]);
  const [pestOptions, setPestOptions] = useState<PestRef[]>([]);
  const [name, setName] = useState(program?.name ?? '');
  const [plannedDate, setPlannedDate] = useState(program?.plannedDate?.substring(0, 10) ?? '');
  const [comments, setComments] = useState(program?.comments ?? '');
  const [cropType, setCropType] = useState(program?.cropType ?? '');
  const [rounds, setRounds] = useState<RoundFormState[]>(
    program?.rounds?.length ? program.rounds.map(hydrateRound) : []
  );

  const [nameError, setNameError] = useState('');
  const [dateError, setDateError] = useState('');
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);

  const initialRounds: RoundFormState[] = program?.rounds?.length
    ? program.rounds.map(hydrateRound)
    : [];

  const { dirty } = useFormDirty(
    {
      name: program?.name ?? '',
      plannedDate: program?.plannedDate?.substring(0, 10) ?? '',
      comments: program?.comments ?? '',
      cropType: program?.cropType ?? '',
      rounds: initialRounds,
    },
    { name, plannedDate, comments, cropType, rounds },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  useEffect(() => {
    sprayService.getProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    listGlobalCropTypes().then(setCropTypes).catch(() => setCropTypes([]));
  }, []);

  useEffect(() => {
    listPests().then(setPestOptions).catch(() => setPestOptions([]));
  }, []);

  // Map productId → product for fast catalog lookups when rendering the
  // read-only dose pair beside each selected product.
  const productMap = useMemo(
    () => new Map(products.map(p => [p._id, p])),
    [products]
  );

  // Crop lineage for dose/PHI rule resolution (program-level picker).
  const cropLineage = useMemo(
    () => lineageOf(cropType || null, cropTypes),
    [cropType, cropTypes]
  );

  const addRound = () => setRounds(prev => [...prev, emptyRound(plannedDate)]);
  const removeRound = (idx: number) => setRounds(prev => prev.filter((_, i) => i !== idx));

  const updateRoundField = (
    idx: number,
    field: 'plannedDate' | 'waterPerHectare' | 'name',
    value: string | number | undefined,
  ) => {
    setRounds(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addProductToRound = (roundIdx: number, productId: string) => {
    if (!productId) return;
    setRounds(prev => prev.map((r, i) => {
      if (i !== roundIdx) return r;
      if (r.products.some(p => p.productId === productId)) return r;
      return { ...r, products: [...r.products, { productId, targetPests: [] }] };
    }));
  };

  const removeProductFromRound = (roundIdx: number, productId: string) => {
    setRounds(prev => prev.map((r, i) => {
      if (i !== roundIdx) return r;
      return { ...r, products: r.products.filter(p => p.productId !== productId) };
    }));
  };

  const updateProductOverride = (roundIdx: number, override: RoundProductOverride) => {
    setRounds(prev => prev.map((r, i) => {
      if (i !== roundIdx) return r;
      return {
        ...r,
        products: r.products.map(p =>
          p.productId === override.productId ? override : p
        ),
      };
    }));
  };

  const handleSave = async () => {
    setNameError('');
    setDateError('');
    setApiError('');

    let valid = true;
    if (!name.trim()) { setNameError(t('spray.programs.nameRequired')); valid = false; }
    if (!plannedDate) { setDateError(t('spray.programs.dateRequired')); valid = false; }
    if (!valid) return;

    setSaving(true);
    const payload = {
      name: name.trim(),
      plannedDate,
      comments: comments || undefined,
      cropType: cropType || null,
      rounds: rounds
        .filter(r => r.products.length > 0 && r.plannedDate)
        .map(r => ({
          products: r.products,
          plannedDate: r.plannedDate,
          waterPerHectare: r.waterPerHectare,
          name: r.name || undefined,
        })),
    };

    try {
      let result: SprayProgram;
      if (isEdit) {
        result = await sprayService.updateProgram(program!._id, payload);
      } else {
        result = await sprayService.createProgram(payload);
      }
      onSave(result);
    } catch (err: any) {
      setApiError(err.message ?? t('spray.programs.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLegacyProgram) return;
    void handleSave();
  };

  // Hoisted section title — TOC entries and FormSection title share the
  // same source of truth (mirrors SprayProductForm convention).
  const titles = {
    details: t('spray.programs.sections.details', { defaultValue: 'Programme details' }),
  };

  // Build the dynamic sections array. The first section is always the
  // programme details; the rest are one section per round. The FormShell
  // long-variant TOC requires sections.length > 1 — when there are zero
  // rounds the form renders the short variant (no TOC). Each round title
  // includes the round number; if the user has named the round we append
  // the name as "Round N: {name}", otherwise just "Round N".
  const sectionDefs = useMemo<FormShellSection[]>(() => {
    const baseSection: FormShellSection = {
      id: 'program-details',
      title: titles.details,
    };
    const roundSections: FormShellSection[] = rounds.map((round, idx) => ({
      id: `program-round-${idx}`,
      title: round.name?.trim()
        ? t('spray.programs.sections.round', { number: idx + 1, name: round.name })
        : t('spray.programs.sections.roundUnnamed', { number: idx + 1 }),
    }));
    return [baseSection, ...roundSections];
  }, [rounds, t, titles.details]);

  // In legacy mode there is no save action — primaryAction is omitted and
  // Close moves to secondaryActions so the user can still leave the form.
  // In normal mode the primary is "Save & close" with a "Cancel" secondary.
  const primaryAction: SaveBarAction | undefined = isLegacyProgram
    ? undefined
    : {
        label: saving
          ? t('patterns.saveBar.primary.saving')
          : apiError
            ? t('patterns.saveBar.primary.retry')
            : t('spray.pageHeader.actions.saveAndClose'),
        onClick: handleSave,
        disabled: (!dirty && !apiError) || saving,
      };
  const secondaryActions: SaveBarAction[] = isLegacyProgram
    ? [{ label: t('common.close'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); } }]
    : [{ label: t('common.cancel'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); }, disabled: saving }];

  // In legacy mode the status area is forced empty — there is nothing to save,
  // so dirty/saving/error states are not meaningful. The dirty hook still runs
  // (we cannot conditionally call it) but its result is intentionally ignored.
  const saveBarStatus: SaveBarStatus = isLegacyProgram
    ? null
    : saving
      ? { kind: 'saving' }
      : apiError
        ? { kind: 'error', message: apiError }
        : dirty
          ? { kind: 'dirty' }
          : null;

  return (
    <div data-testid="spray-program-form">
      <PageHeader
        title={isEdit
          ? t('spray.programs.editTitle', { name: program?.name ?? '', defaultValue: 'Edit {{name}}' })
          : t('spray.pageHeader.actions.newProgram')}
        meta={t('spray.programs.formMeta', {
          defaultValue: 'A spray programme is a sequence of rounds, each with one or more products.',
        })}
      />

      {apiError && (
        <Alert variant="danger" role="alert" className="mb-3">
          {apiError}
        </Alert>
      )}
      {isLegacyProgram && (
        <Alert variant="warning" role="status" className="mb-3">
          {t('spray.programs.legacyEditLocked')}
        </Alert>
      )}

      <FormShell
        sections={sectionDefs}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={saveBarStatus}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
          />
        }
      >
        <FormSection id="program-details" title={titles.details}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('spray.programs.name')}
              required
              controlId="program-name"
              errorText={nameError || undefined}
            >
              <input
                id="program-name"
                type="text"
                className="pat-input"
                value={name}
                onChange={e => setName(e.target.value)}
                aria-invalid={Boolean(nameError) || undefined}
                aria-required="true"
                disabled={isLegacyProgram}
              />
            </Field>
            <Field
              label={t('spray.programs.plannedDate')}
              required
              controlId="program-date"
              errorText={dateError || undefined}
            >
              <input
                id="program-date"
                type="date"
                className="pat-input"
                value={plannedDate}
                onChange={e => setPlannedDate(e.target.value)}
                aria-invalid={Boolean(dateError) || undefined}
                aria-required="true"
                disabled={isLegacyProgram}
              />
            </Field>
          </div>
          <Field
            label={t('spray.programs.comments')}
            controlId="program-comments"
          >
            <textarea
              id="program-comments"
              rows={2}
              className="pat-input"
              value={comments}
              onChange={e => setComments(e.target.value)}
              disabled={isLegacyProgram}
            />
          </Field>
          <Field
            label={t('spray.programs.cropTypeLabel')}
            controlId="program-crop-type"
            helpText={t('spray.programs.cropTypeHint')}
          >
            <select
              id="program-crop-type"
              className="pat-input"
              value={cropType}
              onChange={e => setCropType(e.target.value)}
              disabled={isLegacyProgram}
            >
              <option value="">{t('spray.programs.cropTypeNone')}</option>
              {cropTypes.map(ct => (
                <option key={ct._id} value={ct.code}>{ct.name}</option>
              ))}
            </select>
          </Field>
        </FormSection>

        {rounds.map((round, idx) => {
          const selectedProductIds = new Set(round.products.map(p => p.productId));
          const remainingProducts = products.filter(
            p => !selectedProductIds.has(p._id)
          );
          const sectionTitle = round.name?.trim()
            ? t('spray.programs.sections.round', { number: idx + 1, name: round.name })
            : t('spray.programs.sections.roundUnnamed', { number: idx + 1 });
          const roundNameId = `program-round-${idx}-name`;
          const roundDateId = `program-round-${idx}-date`;
          const roundWaterId = `program-round-${idx}-water`;
          const roundAddProductId = `program-round-${idx}-add-product`;
          return (
            <FormSection
              key={round._id ?? idx}
              id={`program-round-${idx}`}
              title={sectionTitle}
            >
              <div className="pat-field-grid pat-field-grid--3">
                <Field
                  label={t('spray.programs.roundName')}
                  controlId={roundNameId}
                >
                  <input
                    id={roundNameId}
                    type="text"
                    className="pat-input"
                    value={round.name ?? ''}
                    onChange={e => updateRoundField(idx, 'name', e.target.value)}
                    disabled={isLegacyProgram}
                  />
                </Field>
                <Field
                  label={t('spray.programs.roundDate')}
                  controlId={roundDateId}
                >
                  <input
                    id={roundDateId}
                    type="date"
                    className="pat-input"
                    value={round.plannedDate}
                    onChange={e => updateRoundField(idx, 'plannedDate', e.target.value)}
                    disabled={isLegacyProgram}
                  />
                </Field>
                <Field
                  label={t('spray.programs.roundWater')}
                  controlId={roundWaterId}
                >
                  <input
                    id={roundWaterId}
                    type="number"
                    className="pat-input"
                    value={round.waterPerHectare ?? ''}
                    onChange={e => updateRoundField(
                      idx,
                      'waterPerHectare',
                      e.target.value ? parseFloat(e.target.value) : undefined,
                    )}
                    disabled={isLegacyProgram}
                  />
                </Field>
              </div>

              <Field
                label={t('spray.programs.roundProduct')}
                controlId={roundAddProductId}
              >
                <div>
                  {/* Selected products: header row + inline override editor. */}
                  {round.products.length > 0 && (
                    <ul className="list-unstyled mb-2">
                      {round.products.map(entry => {
                        const pid = entry.productId;
                        const product = productMap.get(pid);
                        const dose = product?.dosePer100L;
                        const unit = product?.dosePer100LUnit;
                        const dosePair = (dose != null && unit)
                          ? `${dose} ${unit}/100L`
                          : '—';

                        // Rule-based dose/PHI preview — uses usageRules from the
                        // already-fetched product catalog. Only rendered when a
                        // program-level cropType is selected AND the product doc
                        // has usageRules (i.e. not legacy scalar-only products).
                        let rulePreview: string | null = null;
                        if (cropType && product && Array.isArray(product.usageRules)) {
                          const resolved = resolveSprayApplication({
                            product,
                            roundProductEntry: entry,
                            orchard: { cropType },
                            cropLineage,
                            targetPests: entry.targetPests || [],
                            round: { waterPerHectare: round.waterPerHectare },
                          });
                          if (resolved.resolvedFrom === 'unresolved') {
                            rulePreview = t('spray.programs.rulePreviewNone');
                          } else {
                            const doseStr = resolved.dose
                              ? `${resolved.dose.value} ${resolved.dose.unit}/${resolved.dose.denominator}`
                              : null;
                            const phiStr = resolved.phi
                              ? `PHI ${resolved.phi.value} ${resolved.phi.unit}`
                              : null;
                            rulePreview = [doseStr, phiStr].filter(Boolean).join(' · ') || t('spray.programs.rulePreviewNone');
                          }
                        }

                        return (
                          <li key={pid} className="mb-3">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="flex-grow-1">
                                <span>{product?.productName || pid}</span>
                                <span className="text-muted ms-2 small">{dosePair}</span>
                                {rulePreview !== null && (
                                  <span className="text-muted ms-2 small fst-italic">({rulePreview})</span>
                                )}
                              </span>
                              <button
                                type="button"
                                className="pat-btn pat-btn--subtle"
                                aria-label={t('spray.programs.removeProduct')}
                                onClick={() => removeProductFromRound(idx, pid)}
                                disabled={isLegacyProgram}
                              >
                                ×
                              </button>
                            </div>
                            {!isLegacyProgram && (
                              <div className="ps-2 border-start border-2">
                                <RoundProductOverrideEditor
                                  value={entry}
                                  onChange={override => updateProductOverride(idx, override)}
                                  pestOptions={pestOptions}
                                />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {/* Add-product picker filters out already-selected products. */}
                  {!isLegacyProgram && (
                    <select
                      id={roundAddProductId}
                      className="pat-input"
                      aria-label={t('spray.programs.addProductToRound')}
                      value=""
                      onChange={e => {
                        addProductToRound(idx, e.target.value);
                        // Reset the select so the placeholder shows again.
                        e.target.value = '';
                      }}
                    >
                      <option value="">{t('spray.programs.addProductToRound')}</option>
                      {remainingProducts.map(p => (
                        <option key={p._id} value={p._id}>{p.productName}</option>
                      ))}
                    </select>
                  )}
                </div>
              </Field>

              <div className="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  className="pat-btn pat-btn--danger"
                  onClick={() => removeRound(idx)}
                  disabled={isLegacyProgram}
                >
                  {t('spray.programs.removeRound')}
                </button>
              </div>
            </FormSection>
          );
        })}

        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={addRound}
          disabled={isLegacyProgram}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.programs.addRound')}
        </button>
      </FormShell>
    </div>
  );
};

export default SprayProgramForm;
